import imageCompression from 'browser-image-compression';

// ─────────────────────────────────────────────────────────────────────
// Adaptive client-side image compression
// ─────────────────────────────────────────────────────────────────────
// Web standard for school-website photos:
//   - max 2000 px on the longest edge (matches server's Sharp pipeline)
//   - target ≤ 500 KB after compression
//   - quality picked dynamically by browser-image-compression to hit
//     the size target without dropping below 0.6
//   - format preserved (server later produces WebP variants anyway)
//
// Files that already meet the target are returned unchanged so we
// don't waste cycles re-encoding tiny logos / pre-optimised assets.
// ─────────────────────────────────────────────────────────────────────

const MAX_DIMENSION = 2000;
const TARGET_SIZE_MB = 0.5;
const SKIP_BELOW_BYTES = 350 * 1024; // 350 KB — small enough to send raw

const ACCEPTED_TYPES = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/gif',
]);

/**
 * Compress one image in the browser before uploading.
 *
 *   const out = await compressImage(file, { onProgress(percent) {...} });
 *   // out: { file: File, originalSize, compressedSize, ratio, skipped }
 *
 * Throws if the file isn't a supported image MIME type.
 */
export async function compressImage(file, { onProgress } = {}) {
  if (!ACCEPTED_TYPES.has(file.type)) {
    throw new Error(`Unsupported image type: ${file.type || 'unknown'}`);
  }

  const originalSize = file.size;

  // Tiny files: skip compression entirely.
  if (originalSize <= SKIP_BELOW_BYTES) {
    onProgress?.(100);
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      ratio: 1,
      skipped: true,
    };
  }

  const compressed = await imageCompression(file, {
    maxSizeMB: TARGET_SIZE_MB,
    maxWidthOrHeight: MAX_DIMENSION,
    useWebWorker: true,
    initialQuality: 0.82,
    alwaysKeepResolution: false,
    fileType: file.type, // keep extension; server handles WebP variant
    onProgress: (pct) => onProgress?.(pct),
  });

  // browser-image-compression returns a Blob in some paths; coerce back
  // to a File so the multipart upload retains the filename.
  const out = compressed instanceof File
    ? compressed
    : new File([compressed], file.name, { type: compressed.type || file.type, lastModified: Date.now() });

  return {
    file: out,
    originalSize,
    compressedSize: out.size,
    ratio: out.size / originalSize,
    skipped: false,
  };
}

/** Pretty-print bytes for UI ("3.2 MB", "380 KB"). */
export function formatBytes(bytes) {
  if (bytes === 0 || bytes == null) return '—';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
